import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { pmApi } from '../../api/pm.js';
import { jiraApi } from '../../api/jira.js';

const TABS = ['General', 'Components', 'Versions', 'Labels', 'Issue types', 'Workflows'];

export default function ProjectSettings() {
  const { key } = useParams();
  const [project, setProject] = useState(null);
  const [tab, setTab] = useState('General');

  useEffect(() => { pmApi.getProject(key).then((d) => setProject(d?.project)); }, [key]);
  if (!project) return <div className="muted">Loading…</div>;

  return (
    <section>
      <header className="page-header">
        <div>
          <p className="muted small"><Link to={`/pm/projects/${project.key}/board`}>← Board</Link></p>
          <h1>{project.name} — Settings</h1>
        </div>
      </header>
      <div className="tabs">
        {TABS.map((t) => (
          <button key={t} className={tab === t ? 'tab active' : 'tab'} onClick={() => setTab(t)}>{t}</button>
        ))}
      </div>
      {tab === 'General'      && <General project={project} />}
      {tab === 'Components'   && <Components project={project} />}
      {tab === 'Versions'     && <Versions project={project} />}
      {tab === 'Labels'       && <Labels project={project} />}
      {tab === 'Issue types'  && <IssueTypes project={project} />}
      {tab === 'Workflows'    && <Workflows project={project} />}
    </section>
  );
}

function General({ project }) {
  return (
    <div className="card">
      <h3>General</h3>
      <dl className="kv">
        <dt>Key</dt><dd><code>{project.key}</code></dd>
        <dt>Name</dt><dd>{project.name}</dd>
        <dt>Description</dt><dd className="muted">{project.description || '—'}</dd>
        <dt>Status</dt><dd><span className={`chip status-${project.status === 'active' ? 'active' : 'cancelled'}`}>{project.status}</span></dd>
        <dt>Visibility</dt><dd>{project.visibility || 'org'}</dd>
        <dt>Category</dt><dd>{project.category || '—'}</dd>
      </dl>
    </div>
  );
}

function Components({ project }) {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [error, setError] = useState(null);
  function refresh() { jiraApi.components(project.id).then((d) => setItems(d?.items || [])); }
  useEffect(refresh, [project.id]);

  async function submit(e) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    try {
      await jiraApi.createComponent(project.id, { name: fd.get('name'), description: fd.get('description') || null });
      e.currentTarget.reset(); setShowForm(false); refresh();
    } catch (err) { setError(err.response?.data?.message || 'Failed'); }
  }
  async function remove(id) {
    if (!window.confirm('Delete component? Issues using it will lose the assignment.')) return;
    await jiraApi.deleteComponent(id); refresh();
  }
  return (
    <div className="card">
      <header className="page-header"><h3>Components</h3><button className="btn primary" onClick={() => setShowForm((s) => !s)}>{showForm ? 'Close' : '+ New'}</button></header>
      {error && <div className="alert error">{error}</div>}
      {showForm && (
        <form onSubmit={submit} className="grid-form">
          <label>Name<input name="name" required /></label>
          <label className="full">Description<input name="description" /></label>
          <div className="full actions"><button className="btn primary" type="submit">Create</button></div>
        </form>
      )}
      <table className="data-table">
        <thead><tr><th>Name</th><th>Lead</th><th>Default assignee</th><th></th></tr></thead>
        <tbody>
          {items.map((c) => (
            <tr key={c.id}>
              <td><strong>{c.name}</strong> {c.description && <span className="muted small">— {c.description}</span>}</td>
              <td className="muted">{c.lead_name || '—'}</td>
              <td className="muted">{c.default_assignee_name || '—'}</td>
              <td><button className="btn ghost small" onClick={() => remove(c.id)}>Delete</button></td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={4} className="muted">No components.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function Versions({ project }) {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  function refresh() { jiraApi.versions(project.id).then((d) => setItems(d?.items || [])); }
  useEffect(refresh, [project.id]);

  async function submit(e) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    await jiraApi.createVersion(project.id, {
      name: fd.get('name'), description: fd.get('description') || null,
      releaseDate: fd.get('releaseDate') || null,
    });
    e.currentTarget.reset(); setShowForm(false); refresh();
  }
  async function release(id) {
    await jiraApi.releaseVersion(id); refresh();
  }
  return (
    <div className="card">
      <header className="page-header"><h3>Versions</h3><button className="btn primary" onClick={() => setShowForm((s) => !s)}>{showForm ? 'Close' : '+ New version'}</button></header>
      {showForm && (
        <form onSubmit={submit} className="grid-form">
          <label>Name<input name="name" required placeholder="1.2.0" /></label>
          <label>Release date<input name="releaseDate" type="date" /></label>
          <label className="full">Description<input name="description" /></label>
          <div className="full actions"><button className="btn primary" type="submit">Create</button></div>
        </form>
      )}
      <table className="data-table">
        <thead><tr><th>Version</th><th>Date</th><th>Issues</th><th>Status</th><th></th></tr></thead>
        <tbody>
          {items.map((v) => (
            <tr key={v.id}>
              <td><strong>{v.name}</strong></td>
              <td>{v.release_date || '—'}</td>
              <td>{v.done_count || 0} / {v.issue_count || 0}</td>
              <td><span className={`chip status-${v.status === 'released' ? 'approved' : 'pending'}`}>{v.status}</span></td>
              <td>{v.status === 'unreleased' && <button className="btn primary small" onClick={() => release(v.id)}>Release</button>}</td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={5} className="muted">No versions.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function Labels({ project }) {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  function refresh() { jiraApi.labels(project.id).then((d) => setItems(d?.items || [])); }
  useEffect(refresh, [project.id]);
  async function submit(e) {
    e.preventDefault();
    const fd = new FormData(e.currentTarget);
    await jiraApi.createLabel(project.id, { name: fd.get('name'), color: fd.get('color') || '#94a3b8' });
    e.currentTarget.reset(); setShowForm(false); refresh();
  }
  return (
    <div className="card">
      <header className="page-header"><h3>Labels</h3><button className="btn primary" onClick={() => setShowForm((s) => !s)}>{showForm ? 'Close' : '+ New label'}</button></header>
      {showForm && (
        <form onSubmit={submit} className="grid-form">
          <label>Name<input name="name" required /></label>
          <label>Color<input name="color" type="color" defaultValue="#94a3b8" /></label>
          <div className="full actions"><button className="btn primary" type="submit">Create</button></div>
        </form>
      )}
      <div className="chips">
        {items.map((l) => <span key={l.id} className="chip" style={{ background: l.color, color: '#fff' }}>{l.name}</span>)}
        {items.length === 0 && <span className="muted small">No labels yet.</span>}
      </div>
    </div>
  );
}

function IssueTypes({ project }) {
  const [items, setItems] = useState([]);
  useEffect(() => { jiraApi.issueTypes(project.id).then((d) => setItems(d?.items || [])); }, [project.id]);
  return (
    <div className="card">
      <h3>Issue types</h3>
      <table className="data-table">
        <thead><tr><th></th><th>Name</th><th>Description</th><th>Subtask?</th><th>Default?</th></tr></thead>
        <tbody>
          {items.map((t) => (
            <tr key={t.id}>
              <td><span className="tag-swatch" style={{ background: t.color }} /></td>
              <td><strong>{t.name}</strong></td>
              <td className="muted">{t.description}</td>
              <td>{t.is_subtask_type ? '✓' : ''}</td>
              <td>{t.is_default ? '✓' : ''}</td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={5} className="muted">No issue types yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function Workflows({ project }) {
  const [items, setItems] = useState([]);
  useEffect(() => { jiraApi.workflows(project.id).then((d) => setItems(d?.items || [])); }, [project.id]);
  return (
    <div className="card">
      <h3>Workflows</h3>
      {items.length === 0 && <div className="muted">No workflows configured.</div>}
      {items.map((wf) => {
        const statuses = typeof wf.statuses === 'string' ? JSON.parse(wf.statuses) : (wf.statuses || []);
        const transitions = typeof wf.transitions === 'string' ? JSON.parse(wf.transitions) : (wf.transitions || []);
        return (
          <div key={wf.id} style={{ marginBottom: 16 }}>
            <h4>{wf.name} {wf.is_default && <span className="chip status-active">default</span>}</h4>
            <div className="stage-bar">
              {statuses.map((s) => (
                <div key={s.id} className={`stage-step done`} style={{ '--c': s.color }}>
                  <div className="dot" style={{ background: s.color, borderColor: s.color }} />
                  <span>{s.name}</span>
                </div>
              ))}
            </div>
            <ul className="dep-list" style={{ marginTop: 8 }}>
              {transitions.map((tr, i) => (
                <li key={i}><span className="chip">{tr.name}</span> <code>{tr.from_status_id}</code> → <code>{tr.to_status_id}</code></li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}
